import React, { useState, useEffect } from 'react';
import {
  Share2,
  Lock,
  Unlock,
  Users,
  Check,
  X,
  User,
  ChevronDown
} from 'lucide-react';
import { useSharingContext } from '../../contexts/SharingContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { toast } from 'react-hot-toast';

/**
 * Production-ready share button for college application content
 * Integrates with real sharing system and family context
 */
const ShareButton = ({
  itemType,
  itemId,
  itemTitle,
  currentStatus = 'draft',
  className = '',
  variant = 'default', // 'default', 'compact', 'icon-only'
  onShareComplete
}) => {
  const { shareItem, getShareStatus, getAvailableParents } = useSharingContext();
  const { familyMembers } = useFamilyContext();

  const [isShared, setIsShared] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [shareOptions, setShareOptions] = useState({
    version: currentStatus,
    allowComments: true,
    notifyOnView: false
  });
  const [loading, setLoading] = useState(false);
  const [availableParents, setAvailableParents] = useState([]);

  // Load available parents when component mounts
  useEffect(() => {
    const loadParents = async () => {
      try {
        const parents = await getAvailableParents();
        setAvailableParents(parents);
      } catch (error) {
        console.error('Error loading parents:', error);
        setAvailableParents([]);
      }
    };

    loadParents();
  }, [getAvailableParents]);

  // Check if item is already shared
  useEffect(() => {
    const status = getShareStatus(itemType, itemId);
    if (status) {
      setIsShared(status.isShared);
    }
  }, [itemType, itemId, getShareStatus]);

  const handleShare = async () => {
    console.log('🔵 ShareButton: Starting share process', {
      itemType,
      itemId,
      itemTitle,
      selectedRecipients,
      shareOptions
    });

    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one parent to share with');
      return;
    }

    setLoading(true);
    try {
      console.log('🔵 ShareButton: Calling shareItem with:', {
        itemType,
        itemId,
        selectedRecipients,
        options: {
          ...shareOptions,
          itemTitle,
          metadata: {
            status: currentStatus,
            sharedAt: new Date().toISOString()
          }
        }
      });

      const result = await shareItem(itemType, itemId, selectedRecipients, {
        ...shareOptions,
        itemTitle,
        metadata: {
          status: currentStatus,
          sharedAt: new Date().toISOString()
        }
      });

      console.log('🔵 ShareButton: Share result:', result);

      setIsShared(true);
      setShowModal(false);
      toast.success('Successfully shared with parents');

      if (onShareComplete) {
        onShareComplete({
          itemId,
          recipients: selectedRecipients,
          options: shareOptions
        });
      }
    } catch (error) {
      console.error('🔴 ShareButton: Error sharing item:', error);
      toast.error('Failed to share. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleRecipient = (recipientId) => {
    setSelectedRecipients(prev =>
      prev.includes(recipientId)
        ? prev.filter(id => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const ShareModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-200 bg-gradient-to-r from-azure-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-stone-900">Share with Parents</h2>
            <button
              onClick={() => setShowModal(false)}
              className="p-2 hover:bg-white/80 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={20} className="text-stone-500" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Item Info */}
            <div className="bg-gradient-to-r from-stone-50 to-stone-100 rounded-xl p-4">
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">Sharing:</p>
              <p className="font-semibold text-stone-900 text-lg">{itemTitle}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-sm text-stone-600">
                  Type: <span className="font-medium">{itemType.replace('_', ' ')}</span>
                </span>
                <span className="text-stone-400">•</span>
                <span className="text-sm text-stone-600">
                  Status: <span className="font-medium capitalize">{currentStatus}</span>
                </span>
              </div>
            </div>

            {/* Recipients Selection */}
            <div>
              <label className="text-sm font-semibold text-stone-700 mb-3 block">
                Share with:
              </label>
              {availableParents.length === 0 ? (
                <p className="text-sm text-stone-500 italic">No parents found in your family</p>
              ) : (
                <div className="space-y-2">
                  {availableParents.map(parent => (
                    <div
                      key={parent.id}
                      onClick={() => toggleRecipient(parent.id)}
                      className={`flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedRecipients.includes(parent.id)
                          ? 'border-azure-500 bg-azure-50'
                          : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          selectedRecipients.includes(parent.id)
                            ? 'bg-azure-500 border-azure-500'
                            : 'bg-white border-stone-300'
                        }`}>
                          {selectedRecipients.includes(parent.id) && (
                            <Check size={14} className="text-white" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">{parent.name}</p>
                          <p className="text-sm text-stone-500">{parent.email}</p>
                        </div>
                      </div>
                      <User size={18} className="text-stone-400" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Share Options */}
            <div className="space-y-3">
              <label className="flex items-center justify-between p-4 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={shareOptions.allowComments}
                    onChange={(e) => setShareOptions(prev => ({
                      ...prev,
                      allowComments: e.target.checked
                    }))}
                    className="w-5 h-5 text-azure-600 rounded border-stone-300 focus:ring-azure-500"
                  />
                  <div>
                    <span className="font-medium text-stone-900">Allow comments</span>
                    <p className="text-xs text-stone-500 mt-0.5">Parents can provide feedback</p>
                  </div>
                </div>
              </label>

              <label className="flex items-center justify-between p-4 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={shareOptions.notifyOnView}
                    onChange={(e) => setShareOptions(prev => ({
                      ...prev,
                      notifyOnView: e.target.checked
                    }))}
                    className="w-5 h-5 text-azure-600 rounded border-stone-300 focus:ring-azure-500"
                  />
                  <div>
                    <span className="font-medium text-stone-900">Notify when viewed</span>
                    <p className="text-xs text-stone-500 mt-0.5">Get notified when parents view this</p>
                  </div>
                </div>
              </label>
            </div>

            {/* Version Selection (for essays) */}
            {itemType === 'essay' && (
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-2 block">
                  Share version:
                </label>
                <select
                  value={shareOptions.version}
                  onChange={(e) => setShareOptions(prev => ({
                    ...prev,
                    version: e.target.value
                  }))}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                >
                  <option value="current">Current Version ({currentStatus})</option>
                  <option value="draft">Draft Version</option>
                  <option value="final">Final Version (if available)</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-200 bg-stone-50 flex items-center justify-end gap-3">
          <button
            onClick={() => setShowModal(false)}
            className="px-5 py-2.5 text-stone-700 font-medium hover:bg-stone-200 rounded-lg transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleShare}
            disabled={selectedRecipients.length === 0 || loading}
            className={`px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-all ${
              selectedRecipients.length === 0 || loading
                ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                : 'bg-azure-600 text-white hover:bg-azure-700 shadow-sm hover:shadow'
            }`}
          >
            <Share2 size={16} />
            {loading ? 'Sharing...' : 'Share'}
          </button>
        </div>
      </div>
    </div>
  );

  // Render different button variants
  if (variant === 'icon-only') {
    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          className={`p-2 rounded-lg transition-all ${
            isShared
              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
          } ${className}`}
          title={isShared ? 'Shared with parents' : 'Share with parents'}
        >
          {isShared ? <Unlock size={18} /> : <Lock size={18} />}
        </button>
        {showModal && <ShareModal />}
      </>
    );
  }

  if (variant === 'compact') {
    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
            isShared
              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
          } ${className}`}
        >
          {isShared ? <Unlock size={14} /> : <Lock size={14} />}
          <span>{isShared ? 'Shared' : 'Share'}</span>
        </button>
        {showModal && <ShareModal />}
      </>
    );
  }

  // Default variant
  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`px-4 py-2 rounded-lg flex items-center gap-2 font-medium transition-all ${
          isShared
            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
        } ${className}`}
      >
        {isShared ? (
          <>
            <Unlock size={16} />
            <span>Shared with Parents</span>
            <Check size={14} />
          </>
        ) : (
          <>
            <Lock size={16} />
            <span>Share with Parents</span>
          </>
        )}
      </button>
      {showModal && <ShareModal />}
    </>
  );
};

export default ShareButton;